/**
 * F-022: Mutation hook for delivering a shipped order (shipped → delivered).
 *
 * Wraps `deliverOrderTransition` (F-023 alias). On success (result.success is
 * true): updates the detail cache and invalidates the store order list.
 * On business failure or network error: fires a toast with the mapped key.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { deliverOrderTransition, mapTransitionError, useTranslation } from '@dukanti/shared';
import type { TransitionResult } from '@dukanti/shared';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

export interface UseDeliverOrderOptions {
  readonly orderId: string;
  readonly storeId: string | null;
}

/**
 * Mark a shipped order as delivered.
 *
 * On success: `setQueryData` from RPC's returned order row + invalidate list.
 * On business failure or network error: fires `toast.error` with the mapped key.
 */
export function useDeliverOrder(
  opts: UseDeliverOrderOptions,
): UseMutationResult<TransitionResult, unknown, void> {
  const { orderId, storeId } = opts;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<TransitionResult, unknown, void>({
    mutationFn: () => deliverOrderTransition(supabase, orderId),
    onSuccess: (result) => {
      if (!result.success) {
        const key = mapTransitionError(undefined, result, 'deliver');
        toast.error(t(key));
        return;
      }

      if (result.order) {
        queryClient.setQueryData(
          ['admin-order-detail', orderId],
          (old: { order: unknown; items: unknown; events: unknown } | undefined) =>
            old ? { ...old, order: result.order } : old,
        );
      }
      if (storeId) {
        void queryClient.invalidateQueries({ queryKey: ['store-orders', storeId] });
      }
      void queryClient.invalidateQueries({ queryKey: ['incoming-orders-count-user'] });
      toast.success(t('admin.orders.actions.successToasts.delivered'));
    },
    onError: (err) => {
      const key = mapTransitionError(err, undefined, 'deliver');
      toast.error(t(key));
    },
  });
}
